import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import { BarChart3, FolderOpen, Wallet } from 'lucide-react';
import { ClientDto } from '../../services/clientsService';
import { billingApi, BillingSummaryDto } from '../../services/paymentsService';
import { processStagesApi, ProcessStageDto, committeesApi, CommitteeDto } from '../../services/committeesService';
import { reminderApi, Reminder } from '../../services/reminderService';
import { appointmentsApi, Appointment } from '../../services/appointmentsService';
import folderDocumentsApi, { FolderDocumentDto, FolderDocumentRequestDto, FolderDocumentRequestStatus } from '../../services/folderDocumentService';
import StatusCard from '../overview/StatusCard';
import CompactTimeline from '../overview/CompactTimeline';
import UrgentTasksList from '../overview/UrgentTasksList';
import UpcomingEventsList from '../overview/UpcomingEventsList';
import DiseasesCard from '../overview/DiseasesCard';

interface OverviewTabProps {
  client: ClientDto;
  formatDateOnly: (dateString?: string) => string;
  formatDateTime: (dateString?: string) => string;
  onNavigateToTab?: (tabKey: string) => void;
}

const Container = styled.div`
  direction: rtl;
  padding: 24px;

  @media (max-width: 767px) {
    padding: 16px;
  }
`;

const Section = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 32px;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  & > * {
    min-width: 0;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  font-size: 16px;
  color: var(--text-secondary);
`;

const ErrorMessage = styled.div`
  background: var(--error-light);
  color: #dc2626;
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  margin-bottom: 20px;
  border: 1px solid #dc2626;
`;

const OverviewTab: React.FC<OverviewTabProps> = ({
  client,
  formatDateOnly,
  formatDateTime,
  onNavigateToTab
}) => {
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [billingSummary, setBillingSummary] = useState<BillingSummaryDto | null>(null);
  const [stages, setStages] = useState<ProcessStageDto[]>([]);
  const [urgentReminders, setUrgentReminders] = useState<Reminder[]>([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState<Appointment[]>([]);
  const [upcomingCommittees, setUpcomingCommittees] = useState<CommitteeDto[]>([]);
  const [documents, setDocuments] = useState<FolderDocumentDto[]>([]);
  const [documentRequests, setDocumentRequests] = useState<FolderDocumentRequestDto[]>([]);

  const loadAllData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all data in parallel
      const [
        billing,
        processStages,
        reminders,
        appointments,
        committees,
        clientDocs,
        docRequests
      ] = await Promise.all([
        billingApi.getSummary(client.id).catch(() => null),
        processStagesApi.getByClientId(client.id).catch(() => []),
        reminderApi.getClientReminders(client.id).catch(() => []),
        appointmentsApi.getClientAppointments(client.id).catch(() => []),
        committeesApi.getByClientId(client.id).catch(() => []),
        folderDocumentsApi.getDocumentsByClient(client.id).catch(() => []),
        folderDocumentsApi.getRequestsByClient(client.id).catch(() => [])
      ]);

      setBillingSummary(billing);
      setStages(processStages);
      setDocuments(clientDocs);
      setDocumentRequests(docRequests);

      // Filter urgent reminders (today and overdue)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const urgent = reminders.filter(r => {
        const dueDate = new Date(r.dueDateTime);
        return r.status === 1 && dueDate < tomorrow; // Status 1 = Pending
      }).sort((a, b) => a.priority - b.priority); // Sort by priority (1=high, 2=medium, 3=low)

      setUrgentReminders(urgent);

      // Filter upcoming appointments (not completed/cancelled)
      const upcoming = appointments
        .filter(a => a.status !== 3 && a.status !== 4 && a.appointmentDateTime) // Not Completed or Cancelled
        .sort((a, b) => {
          if (!a.appointmentDateTime) return 1;
          if (!b.appointmentDateTime) return -1;
          return new Date(a.appointmentDateTime).getTime() - new Date(b.appointmentDateTime).getTime();
        })
        .slice(0, 3);

      setUpcomingAppointments(upcoming);

      // Filter upcoming committees
      const now = new Date();
      const upcomingComm = committees
        .filter(c => new Date(c.committeeDate) >= now)
        .sort((a, b) => new Date(a.committeeDate).getTime() - new Date(b.committeeDate).getTime())
        .slice(0, 2);

      setUpcomingCommittees(upcomingComm);

    } catch (err) {
      console.error('Error loading overview data:', err);
      setError('שגיאה בטעינת נתוני הסקירה');
    } finally {
      setLoading(false);
    }
  }, [client.id]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  const handleCompleteReminder = async (reminderId: number) => {
    try {
      await reminderApi.handleReminder(reminderId, { handledNotes: 'הושלם מסקירה כללית' });
      loadAllData();
    } catch (error) {
      console.error('Error completing reminder:', error);
      toast.error('שגיאה בסימון התזכורת כהושלמה');
    }
  };

  const getStatusCardData = () => {
    const { currentStatusName, currentStatusColor } = client;

    if (!currentStatusName || !currentStatusColor) {
      return {
        mainValue: '-',
        subValue: 'אין סטטוס',
        color: 'var(--text-secondary)',
        subtitle: undefined
      };
    }

    return {
      mainValue: currentStatusName,
      subValue: client.treatmentStartDate
        ? `מ-${formatDateOnly(client.treatmentStartDate)}`
        : '',
      color: currentStatusColor,
      subtitle: client.daysInTreatment > 0
        ? `${client.daysInTreatment} ימים בטיפול`
        : undefined
    };
  };

  const getDocumentsCardData = () => {
    const uploaded = documents.length;

    // Calculate required documents from sent document requests (folder-based system)
    const sentRequests = documentRequests.filter(req =>
      req.status === FolderDocumentRequestStatus.Sent ||
      req.status === FolderDocumentRequestStatus.PartiallyReceived ||
      req.status === FolderDocumentRequestStatus.Completed
    );

    // Total items from all sent requests
    const totalRequested = sentRequests.reduce((total, request) =>
      total + request.totalCount, 0
    );

    // Total received from all sent requests
    const totalReceived = sentRequests.reduce((total, request) =>
      total + request.receivedCount, 0
    );

    // If no documents are required, show just uploaded count
    if (totalRequested === 0) {
      return {
        mainValue: uploaded.toString(),
        subValue: 'מסמכים הועלו',
        color: uploaded > 0 ? '#16a34a' : 'var(--text-secondary)',
        subtitle: 'לא נשלחו בקשות מסמכים'
      };
    }

    const percentage = Math.round((totalReceived / totalRequested) * 100);

    let color = '#dc2626';
    if (percentage >= 80) color = '#16a34a';
    else if (percentage >= 50) color = '#d97706';

    return {
      mainValue: `${totalReceived}/${totalRequested}`,
      subValue: `${percentage}% מבקשות התקבל`,
      color,
      subtitle: totalReceived >= totalRequested ? 'כל המסמכים התקבלו' : `נותרו ${totalRequested - totalReceived} מסמכים`
    };
  };

  const getBillingCardData = () => {
    if (!billingSummary || !billingSummary.billingRecord) {
      return {
        mainValue: '-',
        subValue: 'טרם הוזן חיוב',
        color: 'var(--text-secondary)',
        subtitle: undefined
      };
    }

    const { totalCharge, totalPaid, balance } = billingSummary;
    const formattedBalance = `₪${balance.toLocaleString('he-IL', { minimumFractionDigits: 2 })}`;

    let color = '#16a34a';
    let subtitle = 'שולם במלואו';

    if (balance > 0) {
      color = '#d97706';
      subtitle = `נותרו לתשלום ₪${balance.toLocaleString('he-IL')}`;
    }

    return {
      mainValue: formattedBalance,
      subValue: `מתוך ₪${totalCharge.toLocaleString('he-IL')} שולם ₪${totalPaid.toLocaleString('he-IL')}`,
      color,
      subtitle
    };
  };

  if (loading) {
    return (
      <Container>
        <LoadingContainer>טוען נתוני סקירה...</LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>{error}</ErrorMessage>
      </Container>
    );
  }

  const statusData = getStatusCardData();
  const docsData = getDocumentsCardData();
  const billingData = getBillingCardData();

  return (
    <Container>
      {/* Status Cards */}
      <CardsGrid>
        <StatusCard
          title="סטטוס נוכחי"
          mainValue={statusData.mainValue}
          subValue={statusData.subValue}
          color={statusData.color}
          icon={BarChart3}
          subtitle={statusData.subtitle}
          onClick={() => onNavigateToTab?.('client-medical')}
        />

        <StatusCard
          title="מסמכים"
          mainValue={docsData.mainValue}
          subValue={docsData.subValue}
          color={docsData.color}
          icon={FolderOpen}
          subtitle={docsData.subtitle}
          onClick={() => onNavigateToTab?.('documents')}
        />

        <StatusCard
          title="יתרה לתשלום"
          mainValue={billingData.mainValue}
          subValue={billingData.subValue}
          color={billingData.color}
          icon={Wallet}
          subtitle={billingData.subtitle}
          onClick={() => onNavigateToTab?.('billing')}
        />
      </CardsGrid>

      {/* Process Timeline - Full Width */}
      <Section>
        <CompactTimeline
          stages={stages}
          onViewFull={() => onNavigateToTab?.('process-committees')}
        />
      </Section>

      {/* Two-Column Layout: Urgent Tasks | Upcoming Events */}
      <TwoColumnGrid>
        <UrgentTasksList
          reminders={urgentReminders}
          onCompleteReminder={handleCompleteReminder}
          onViewAll={() => onNavigateToTab?.('schedule-tasks')}
        />

        <UpcomingEventsList
          appointments={upcomingAppointments}
          committees={upcomingCommittees}
          onViewAll={() => onNavigateToTab?.('schedule-tasks')}
        />
      </TwoColumnGrid>

      {/* Diseases Card */}
      <Section>
        <DiseasesCard clientId={client.id} />
      </Section>
    </Container>
  );
};

export default OverviewTab;
